"use client";

import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { WomanHolistik as WomanHolistikType } from "@/types/home";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { ArrowLeft, ArrowRight } from "lucide-react";

interface WomanHolistikProps {
      data: WomanHolistikType;
}

export default function WomanHolistikSection({ data }: WomanHolistikProps) {
      if (!data) return null;

      const items = data.item?.slice(0, 5) || [];

      return (
            <section className="relative py-20 bg-white overflow-hidden z-10">
                  <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                        <div className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] bg-[#A68F72]/5 rounded-full blur-[120px]" />
                        <div className="absolute bottom-[-10%] right-[-5%] w-[500px] h-[500px] bg-[#A68F72]/5 rounded-full blur-[120px]" />
                  </div>

                  <div className="container mx-auto px-4 md:px-8 relative z-10">
                        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-8">
                              <div className="max-w-2xl">
                                    {data.badge && (
                                          <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#A68F72]/10 text-[#A68F72] text-[10px] font-bold tracking-[0.15em] uppercase mb-4 border border-[#A68F72]/20">
                                                {data.badge}
                                          </span>
                                    )}
                                    <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 tracking-tight leading-[1.1]">
                                          {data.title}
                                    </h2>
                                    <p className="text-gray-500 text-base md:text-lg leading-relaxed max-w-xl opacity-80">
                                          {data.description}
                                    </p>
                              </div>

                              <div className="hidden md:flex gap-4">
                                    <button className="swiper-prev-custom w-12 h-12 flex items-center justify-center rounded-full border border-gray-100 bg-white text-gray-400 hover:bg-[#A68F72] hover:text-white hover:border-[#A68F72] shadow-sm transition-all duration-300 cursor-pointer">
                                          <ArrowLeft size={20} />
                                    </button>
                                    <button className="swiper-next-custom w-12 h-12 flex items-center justify-center rounded-full border border-gray-100 bg-white text-gray-400 hover:bg-[#A68F72] hover:text-white hover:border-[#A68F72] shadow-sm transition-all duration-300 cursor-pointer">
                                          <ArrowRight size={20} />
                                    </button>
                              </div>
                        </div>

                        <div className="relative">
                              <Swiper
                                    modules={[Navigation, Pagination, Autoplay]}
                                    spaceBetween={20}
                                    slidesPerView={1.2}
                                    navigation={{
                                          prevEl: ".swiper-prev-custom",
                                          nextEl: ".swiper-next-custom",
                                    }}
                                    pagination={{
                                          clickable: true,
                                          dynamicBullets: true,
                                    }}
                                    autoplay={{
                                          delay: 5000,
                                          disableOnInteraction: false,
                                    }}
                                    breakpoints={{
                                          640: { slidesPerView: 2.2 },
                                          1024: { slidesPerView: 4 },
                                          1280: { slidesPerView: 5 },
                                    }}
                                    className="!pb-16 !overflow-visible [&_.swiper-pagination-bullet-active]:bg-[#A68F72]"
                              >
                                    {items.map((item) => {
                                          const slug = item.treatments_list?.slug;
                                          const href = slug ? `/${slug}` : "#";

                                          return (
                                                <SwiperSlide key={item.id}>
                                                      <Link href={href as any} className="block h-full">
                                                            <div className="group relative aspect-[10/14] overflow-hidden rounded-[2rem] bg-gray-50 transition-all duration-500 hover:-translate-y-2 shadow-[0_10px_30px_rgba(0,0,0,0.04)] h-full">
                                                                  {item.icon?.url ? (
                                                                        <Image
                                                                              src={`${process.env.NEXT_PUBLIC_API_URL}${item.icon.url}`}
                                                                              alt={item.title}
                                                                              fill
                                                                              className="object-cover transition-transform duration-1000 ease-out group-hover:scale-110"
                                                                              sizes="(max-width: 768px) 50vw, 20vw"
                                                                        />
                                                                  ) : (
                                                                        <div className="w-full h-full bg-[#f8f5f2]" />
                                                                  )}

                                                                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-75 transition-opacity duration-500" />

                                                                  <div className="absolute inset-0 flex flex-col justify-end p-6 z-20">
                                                                        <h3 className="text-white text-lg md:text-xl font-semibold mb-3 leading-tight tracking-wide">
                                                                              {item.title}
                                                                        </h3>
                                                                        <div className="w-8 h-[2px] bg-[#A68F72] rounded-full transition-all duration-500 group-hover:w-16 group-hover:bg-white" />
                                                                  </div>
                                                            </div>
                                                      </Link>
                                                </SwiperSlide>
                                          );
                                    })}
                              </Swiper>
                        </div>
                  </div>
            </section>
      );
}